import React from "react";
import { Badge, Box, Grid, Text, VStack, IconButton } from "@chakra-ui/react";
import { LuCopy, LuCheck } from "react-icons/lu";
import { TransactionApiItem } from "store/userTransaction/userTransactionType";
import { toaster } from "components/ui/toaster";

function formatINR(n: number) {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    return `₹${n}`;
  }
}

function statusColor(status: string) {
  const s = (status || "").toUpperCase();
  if (s === "SUCCESS") return "green";
  if (s === "ERROR" || s === "FAILED") return "red";
  if (s === "PENDING") return "yellow";
  return "gray";
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  // Example: "21 Dec, 05:24 PM"
  const raw = d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Ensure am/pm uppercase if environment returns lowercase
  return raw.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
}

const CopyButton: React.FC<{ text: string; label: string }> = ({
  text,
  label,
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      toaster.create({
        title: "Copied!",
        description: `${label} copied to clipboard`,
        type: "success",
        duration: 2000,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toaster.create({
        title: "Failed",
        description: "Could not copy to clipboard",
        type: "error",
      });
    }
  };

  return (
    <IconButton
      aria-label={`Copy ${label}`}
      size="xs"
      variant="ghost"
      onClick={handleCopy}
      colorScheme={copied ? "green" : "gray"}
    >
      {copied ? <LuCheck /> : <LuCopy />}
    </IconButton>
  );
};

export const TransactionRow: React.FC<{ tx: TransactionApiItem }> = ({
  tx,
}) => {
  // Your API doesn't say credit/debit. For bill payments we assume debit display.
  const amountText = `${formatINR(Number(tx.amount || 0))}`;

  const primaryTitle =
    tx.status?.toUpperCase() === "SUCCESS"
      ? `Paid • {Biller Name}`
      : tx.status?.toUpperCase() === "PENDING"
        ? `Pending Payment • {Biller Name}`
        : //   tx.status?.toUpperCase() === "FAILED" ?
          `Failed Payment • {Biller Name}`;

  const comment =
    tx.message ||
    tx.responseReason ||
    (tx.responseCode ? `Response Code: ${tx.responseCode}` : "-");

  return (
    <Box px={3} py={3} borderBottomWidth="1px" borderColor="blackAlpha.100">
      <Grid templateColumns={{ base: "1fr", md: "2fr 1fr 1fr 2fr" }} gap={3}>
        {/* TRANSACTIONS column */}
        <VStack align="start" gap={0.5}>
          <Text fontWeight="medium" color="gray.800">
            {primaryTitle}
          </Text>

          {/* Time line (AM/PM uppercase) */}
          <Text fontSize="sm" color="gray.600">
            {formatDateTime(tx.createdAt)}
          </Text>

          <Box ml={2}>
            {/* Order ID with copy button */}
            <Box display="flex" alignItems="center" gap={1}>
              <Text fontSize="xs" color="gray.600">
                Order ID: {tx.orderId}
              </Text>
              <CopyButton text={tx.orderId} label="Order ID" />
            </Box>

            {/* Transaction ID */}
            {/* {tx.transactionId && (
              <Text fontSize="xs" color="gray.600">
                Txn ID: {tx.transactionId}
              </Text>
            )} */}

            {/* Transaction Reference ID with copy button */}
            {tx.txnReferenceId && (
              <Box display="flex" alignItems="center" gap={1}>
                <Text fontSize="xs" color="gray.600">
                  Txn Ref ID: {tx.txnReferenceId}
                </Text>
                <CopyButton
                  text={tx.txnReferenceId}
                  label="Transaction Ref ID"
                />
              </Box>
            )}
          </Box>
        </VStack>

        {/* AMOUNT column */}
        <Box>
          <Text
            fontWeight="semibold"
            color="gray.800"
            // textAlign={{ base: "left", md: "right" }}
          >
            {amountText}
          </Text>
        </Box>

        {/* STATUS column */}
        <Box>
          <Badge colorScheme={statusColor(tx.status)} variant="plain">
            {(tx.status || "-").toUpperCase()}
          </Badge>
        </Box>

        {/* COMMENTS column */}
        <Box>
          <Text fontSize="sm" color="gray.700">
            {comment}
          </Text>
        </Box>
      </Grid>
    </Box>
  );
};

export default TransactionRow;
